import { arrayMove } from '@dnd-kit/sortable';
import type { EFormSchema } from './schema';

export type DropEdge = 'before' | 'after';

export type DropIndicator =
  | { kind: 'field'; overId: string; edge: DropEdge }
  | { kind: 'section'; sectionId: string };

export function findFieldLocation(
  schema: EFormSchema,
  fieldId: string,
): { sectionIndex: number; fieldIndex: number; sectionId: string } | null {
  for (let si = 0; si < schema.sections.length; si++) {
    const fi = schema.sections[si].fields.findIndex((f) => f.id === fieldId);
    if (fi >= 0) {
      return { sectionIndex: si, fieldIndex: fi, sectionId: schema.sections[si].id };
    }
  }
  return null;
}

/** Reorder / move a field onto another field (before/after) or to the end of a section. */
export function moveFieldInSchema(
  schema: EFormSchema,
  activeId: string,
  overId: string,
  edge: DropEdge = 'before',
): EFormSchema | null {
  const from = findFieldLocation(schema, activeId);
  if (!from) return null;

  const overStr = String(overId);
  let toSectionIndex = -1;
  let toFieldIndex = -1;

  if (overStr.startsWith('section-drop-')) {
    const sectionId = overStr.replace('section-drop-', '');
    toSectionIndex = schema.sections.findIndex((s) => s.id === sectionId);
    if (toSectionIndex < 0) return null;
    toFieldIndex = schema.sections[toSectionIndex].fields.length;
  } else {
    const to = findFieldLocation(schema, overStr);
    if (!to) return null;
    toSectionIndex = to.sectionIndex;
    toFieldIndex = edge === 'before' ? to.fieldIndex : to.fieldIndex + 1;
  }

  const sections = schema.sections.map((s) => ({ ...s, fields: [...s.fields] }));

  if (from.sectionIndex === toSectionIndex) {
    let targetIndex = toFieldIndex;
    if (from.fieldIndex < targetIndex) targetIndex -= 1;
    if (targetIndex === from.fieldIndex) return null;
    sections[from.sectionIndex] = {
      ...sections[from.sectionIndex],
      fields: arrayMove(sections[from.sectionIndex].fields, from.fieldIndex, targetIndex),
    };
  } else {
    const [moved] = sections[from.sectionIndex].fields.splice(from.fieldIndex, 1);
    const insertAt = Math.min(Math.max(0, toFieldIndex), sections[toSectionIndex].fields.length);
    sections[toSectionIndex].fields.splice(insertAt, 0, moved);
  }

  return { ...schema, sections };
}

/** Move one or more fields by delta within their section (keyboard). Cross-section not supported. */
export function nudgeFieldsInSchema(
  schema: EFormSchema,
  fieldIds: string[],
  delta: number,
): EFormSchema | null {
  if (!fieldIds.length || delta === 0) return null;
  const loc = findFieldLocation(schema, fieldIds[fieldIds.length - 1]);
  if (!loc) return null;

  const section = schema.sections[loc.sectionIndex];
  const selectedInSection = fieldIds.filter((id) => section.fields.some((f) => f.id === id));
  if (!selectedInSection.length) return null;

  const indices = selectedInSection
    .map((id) => section.fields.findIndex((f) => f.id === id))
    .filter((i) => i >= 0)
    .sort((a, b) => a - b);

  if (delta < 0 && indices[0] === 0) return null;
  if (delta > 0 && indices[indices.length - 1] === section.fields.length - 1) return null;

  const fields = [...section.fields];
  if (delta < 0) {
    for (const i of indices) {
      const tmp = fields[i - 1];
      fields[i - 1] = fields[i];
      fields[i] = tmp;
    }
  } else {
    for (let k = indices.length - 1; k >= 0; k--) {
      const i = indices[k];
      const tmp = fields[i + 1];
      fields[i + 1] = fields[i];
      fields[i] = tmp;
    }
  }

  const sections = schema.sections.map((s, si) =>
    si === loc.sectionIndex ? { ...s, fields } : s,
  );
  return { ...schema, sections };
}

export function rangeSelectFieldIds(
  schema: EFormSchema,
  sectionId: string,
  fromId: string,
  toId: string,
): string[] {
  const section = schema.sections.find((s) => s.id === sectionId);
  if (!section) return [toId];
  const a = section.fields.findIndex((f) => f.id === fromId);
  const b = section.fields.findIndex((f) => f.id === toId);
  if (a < 0 || b < 0) return [toId];
  const lo = Math.min(a, b);
  const hi = Math.max(a, b);
  return section.fields.slice(lo, hi + 1).map((f) => f.id);
}

export const CANVAS_ZOOM_STEPS = [0.75, 0.9, 1, 1.1, 1.25] as const;
export type CanvasZoom = (typeof CANVAS_ZOOM_STEPS)[number];

export function nextZoom(current: number, dir: 1 | -1): number {
  const idx = CANVAS_ZOOM_STEPS.findIndex((z) => Math.abs(z - current) < 0.001);
  const at = idx >= 0 ? idx : 2;
  const next = CANVAS_ZOOM_STEPS[Math.min(CANVAS_ZOOM_STEPS.length - 1, Math.max(0, at + dir))];
  return next;
}
